// Joiner-side audio session. Glues three pre-existing pieces together
// for the browser-only P2P path:
//
//   PeerHub ── DataConnection ── host
//      │
//      ├──→ PING (every 2 s, 8-burst at start) → PONG → ClockSync.addProbe
//      │       so we learn θ = host_perf - our_perf (µs).
//      │
//      └──→ AUDIO_PCM frames → Scheduler.ingest
//              which maps each play_at_us → AudioContext startCtx and
//              schedules sample-exact playback via anchor chaining.
//
// Same NTP-min filter + anchor-chain math used by the LAN/Rust path,
// just running over DataChannel instead of WebSocket. Because every
// joiner derives `startCtx` from the same shared host clock, all
// devices play the same frame at the same wall-clock moment — that's
// what makes the room "play as one speaker."
//
// Note: the host uses raw `performance.now()` as its reference clock
// (not server-Instant relative). That's fine — ClockSync only cares
// that the host clock is *monotonic*; absolute origin cancels out of
// the offset estimator.
//
// `onPlaying` fires the first time a frame survives ingest, so the
// UI can swap from "waiting for audio" to "listening" without us
// having to poke at <audio>.play() autoplay semantics.
//
// The user-gesture / autoplay rule: callers MUST construct this
// session under a user gesture (the joiner's "Tap to play" button)
// and `await ctx.resume()` before letting frames flow in, otherwise
// iOS Safari silently routes audio to the ringer channel.

import { ClockSync } from '@/audio/clockSync'
import { Scheduler } from '@/audio/scheduler'
import type { MeshLink, PeerEvent } from './PeerHub'
import { decode, encodePing } from './wire'

const PROBE_BURST_COUNT = 8
const PROBE_BURST_INTERVAL_MS = 10
const PROBE_STEADY_INTERVAL_MS = 2_000

export interface P2pSessionOptions {
  ctx: AudioContext
  hub: MeshLink
  /** Called once when the first audio frame is scheduled successfully. */
  onPlaying?: () => void
  onDiagnostics?: (rttMs: number, offsetMs: number, outputLatencyMs: number) => void
}

export class P2pSession {
  readonly clock = new ClockSync()
  readonly scheduler: Scheduler

  private hub: MeshLink
  private peerListener: (e: Event) => void
  private burstTimer: ReturnType<typeof setTimeout> | null = null
  private steadyTimer: ReturnType<typeof setInterval> | null = null
  private burstFired = 0
  private hasPlayed = false
  private onPlaying?: () => void
  private onDiagnostics?: (rttMs: number, offsetMs: number, outputLatencyMs: number) => void
  private destroyed = false
  private loggedFirstPong = false

  constructor(opts: P2pSessionOptions) {
    this.hub = opts.hub
    this.scheduler = new Scheduler(opts.ctx, this.clock)
    this.onPlaying = opts.onPlaying
    this.onDiagnostics = opts.onDiagnostics
    this.peerListener = (e: Event) => {
      const ev = (e as CustomEvent<PeerEvent>).detail
      if (ev.type === 'data') this.handleInbound(ev.data)
    }
    this.hub.addEventListener('peer', this.peerListener)
    this.startProbing()
  }

  setVolume(v: number) {
    this.scheduler.setVolume(v)
  }

  setMuted(muted: boolean) {
    this.scheduler.setMuted(muted)
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true
    this.hub.removeEventListener('peer', this.peerListener)
    if (this.burstTimer) clearTimeout(this.burstTimer)
    if (this.steadyTimer) clearInterval(this.steadyTimer)
    this.scheduler.destroy()
  }

  private startProbing() {
    // Burst of 8 probes 10 ms apart, then a steady 2 s cadence. Same
    // schedule as the LAN path's wsClient.
    const burstTick = () => {
      if (this.destroyed) return
      this.sendPing()
      this.burstFired++
      if (this.burstFired < PROBE_BURST_COUNT) {
        this.burstTimer = setTimeout(burstTick, PROBE_BURST_INTERVAL_MS)
      } else {
        this.burstTimer = null
        this.steadyTimer = setInterval(() => this.sendPing(), PROBE_STEADY_INTERVAL_MS)
      }
    }
    burstTick()
  }

  private sendPing() {
    const t0 = BigInt(Math.round(performance.now() * 1000))
    this.hub.sendToHost(encodePing(t0))
  }

  private handleInbound(data: ArrayBuffer) {
    const decoded = decode(data)
    switch (decoded.kind) {
      case 'pong': {
        // T3 = now; T0 we stamped into the matching PING; T1/T2 came back.
        const t3 = BigInt(Math.round(performance.now() * 1000))
        this.clock.addProbe(decoded.clientSendUs, decoded.hostRecvUs, decoded.hostSendUs, t3)
        if (!this.loggedFirstPong) {
          this.loggedFirstPong = true
          // eslint-disable-next-line no-console
          console.info('[Multispeaker join] first PONG received — clock sync started')
        }

        const state = this.clock.state()
        if (state && this.onDiagnostics) {
          const rttMs = Number(state.delta_min_us) / 1000
          const offsetMs = Number(state.theta_us) / 1000
          const outputLatencyMs = this.scheduler.getOutputLatencyMs()
          this.onDiagnostics(rttMs, offsetMs, outputLatencyMs)
        }
        break
      }
      case 'audio': {
        this.scheduler.ingest({
          seq: decoded.seq,
          play_at_us: decoded.playAtUs,
          samples: decoded.samples,
        })
        if (!this.hasPlayed) {
          // eslint-disable-next-line no-console
          console.info(
            `[Multispeaker join] first audio frame received (seq=${decoded.seq}, ${decoded.samples.length} samples)`,
          )
          this.hasPlayed = true
          this.onPlaying?.()
        }
        break
      }
      default:
        // ping / unknown — joiners shouldn't see PING from the host;
        // ignore quietly.
        break
    }
  }
}
